import { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { BrowserMultiFormatReader, IScannerControls } from "@zxing/browser";
import { keyframes } from "@emotion/react";

const scanLine = keyframes`
  from { top: 8%; }
  to { top: 92%; }
`;

interface CodeScannerDialogProps {
  open: boolean;
  onClose: () => void;
  onDetected: (value: string) => void;
  error?: string;
  onRetry?: () => void;
}

/** Reads a supported code format and returns its unmodified text to the caller. */
export default function CodeScannerDialog({
  open,
  onClose,
  onDetected,
  error,
  onRetry,
}: CodeScannerDialogProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const onDetectedRef = useRef(onDetected);
  const openRef = useRef(open);
  const deliveredRef = useRef(false);
  const sessionRef = useRef(0);
  const [cameraRequested, setCameraRequested] = useState(false);
  const [readingImage, setReadingImage] = useState(false);
  const [manualValue, setManualValue] = useState("");
  const [localError, setLocalError] = useState("");

  onDetectedRef.current = onDetected;
  openRef.current = open;

  useEffect(
    () => () => {
      sessionRef.current += 1;
      controlsRef.current?.stop();
    },
    [],
  );

  useEffect(() => {
    sessionRef.current += 1;
    if (open) {
      deliveredRef.current = false;
      setCameraRequested(true);
      setManualValue("");
      setLocalError("");
      setReadingImage(false);
    } else {
      setCameraRequested(false);
      controlsRef.current?.stop();
      controlsRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    if (!open || !cameraRequested) return;
    let cancelled = false;
    if (window.isSecureContext === false) {
      setLocalError(
        "Para escanear en vivo, abrí la aplicación con HTTPS. Desde una IP por HTTP podés tomar una foto o ingresar el código.",
      );
      setCameraRequested(false);
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setLocalError(
        "Este navegador no permite escanear en vivo. Podés tomar una foto o ingresar el código.",
      );
      setCameraRequested(false);
      return;
    }
    const video = videoRef.current;
    if (!video) return;
    const reader = new BrowserMultiFormatReader();
    setLocalError("");
    void reader
      .decodeFromConstraints(
        { audio: false, video: { facingMode: { ideal: "environment" } } },
        video,
        (result, _error, controls) => {
          if (!result || cancelled || deliveredRef.current) return;
          deliveredRef.current = true;
          controls.stop();
          controlsRef.current = null;
          setCameraRequested(false);
          onDetectedRef.current(result.getText());
        },
      )
      .then((controls) => {
        if (cancelled || deliveredRef.current) controls.stop();
        else controlsRef.current = controls;
      })
      .catch(() => {
        if (cancelled) return;
        setCameraRequested(false);
        setLocalError(
          "No se pudo iniciar la cámara. Revisá el permiso o tomá una foto.",
        );
      });
    return () => {
      cancelled = true;
      controlsRef.current?.stop();
      controlsRef.current = null;
    };
  }, [open, cameraRequested]);

  const detect = (value: string) => {
    deliveredRef.current = true;
    controlsRef.current?.stop();
    controlsRef.current = null;
    setCameraRequested(false);
    setLocalError("");
    onDetectedRef.current(value);
  };

  const readImage = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    onRetry?.();
    const session = sessionRef.current;
    controlsRef.current?.stop();
    controlsRef.current = null;
    setCameraRequested(false);
    setReadingImage(true);
    setLocalError("");
    const imageUrl = URL.createObjectURL(file);
    try {
      const result = await new BrowserMultiFormatReader().decodeFromImageUrl(
        imageUrl,
      );
      if (openRef.current && sessionRef.current === session)
        detect(result.getText());
    } catch {
      if (openRef.current && sessionRef.current === session) {
        setLocalError(
          "No se reconoció un código en la imagen. Probá con otra foto.",
        );
      }
    } finally {
      URL.revokeObjectURL(imageUrl);
      if (openRef.current && sessionRef.current === session)
        setReadingImage(false);
    }
  };

  const retryCamera = () => {
    onRetry?.();
    deliveredRef.current = false;
    setLocalError("");
    setCameraRequested(true);
  };

  const prepareFileSelection = () => {
    controlsRef.current?.stop();
    controlsRef.current = null;
    setCameraRequested(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 1, mx: 2 } }}
    >
      <Box sx={{ px: 3, pt: 3, pb: 2.5, textAlign: "center" }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
          Escanear código
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, px: 1 }}
        >
          Apuntá la cámara al código o tomá una foto.
        </Typography>

        <Stack spacing={1.25}>
          {cameraRequested && (
            <Box
              sx={{
                position: "relative",
                width: "100%",
                aspectRatio: "4 / 3",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <Box
                component="video"
                ref={videoRef}
                muted
                playsInline
                sx={{
                  display: "block",
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
              <Box
                aria-hidden="true"
                sx={{
                  position: "absolute",
                  left: "8%",
                  top: "8%",
                  width: "84%",
                  height: 2,
                  bgcolor: "error.main",
                  boxShadow: "0 0 8px 2px rgba(211, 47, 47, 0.8)",
                  pointerEvents: "none",
                  animation: `${scanLine} 2.4s ease-in-out infinite alternate`,
                  "@media (prefers-reduced-motion: reduce)": {
                    animation: "none",
                    top: "50%",
                  },
                }}
              />
            </Box>
          )}
          {(localError || error) && (
            <Alert severity="warning" role="alert" sx={{ textAlign: "left" }}>
              {localError || error}
            </Alert>
          )}
          {!cameraRequested &&
            window.isSecureContext !== false &&
            !!navigator.mediaDevices?.getUserMedia && (
              <Button
                variant="outlined"
                fullWidth
                size="large"
                onClick={retryCamera}
                disabled={readingImage}
                sx={{ py: 1.4 }}
              >
                Reintentar cámara
              </Button>
            )}
          <Button
            component="label"
            variant="outlined"
            fullWidth
            size="large"
            disabled={readingImage}
            onClick={prepareFileSelection}
            sx={{ py: 1.4 }}
          >
            Tomar foto
            <input
              hidden
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(event) => void readImage(event)}
            />
          </Button>
          <TextField
            label="Ingresar código manualmente"
            value={manualValue}
            onChange={(event) => {
              onRetry?.();
              setManualValue(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" && manualValue !== "")
                detect(manualValue);
            }}
            fullWidth
          />
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={() => detect(manualValue)}
            disabled={manualValue === ""}
            sx={{ py: 1.4, fontWeight: 700 }}
          >
            Buscar código
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            fullWidth
            size="large"
            onClick={onClose}
            sx={{ py: 1.4 }}
          >
            Cerrar
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
}
